/**
 * F37 (Wave 2D) — badge-catalog Add / Edit dialog (CMS).
 *
 * Add mode: `code` is editable (lower_snake_case). Edit mode: `code` is
 * read-only (the badge is keyed by code; PATCH is by id and omits code).
 *
 * Fields: code, name (en), description (en, optional), icon (curated picker —
 * D13), category Select, sport-scope multi-select (null = all sports), active
 * Switch, sort_order NumberInput. Validates against `UpsertBadgeCatalogInput`.
 *
 * Composes F02 primitives only (Modal / FormField / TextInput / Textarea /
 * Select / Switch / NumberInput / Button / Banner) + the local BadgeIconPicker
 * + SportScopePicker.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  FormField,
  Modal,
  NumberInput,
  Select,
  Switch,
  Textarea,
  TextInput,
} from '@bravemove/ui-web';
import {
  UpsertBadgeCatalogInputSchema,
  type BadgeCatalogEntry,
  type BadgeCategory,
  type SportName,
  type UpsertBadgeCatalogInput,
} from '@bravemove/types';
import { BadgeIconPicker } from './BadgeIconPicker';
import { SportScopePicker } from './SportScopePicker';

const BADGE_CATEGORY_VALUES: readonly BadgeCategory[] = ['sport', 'skill', 'award', 'milestone'];

export interface BadgeCatalogEditDialogProps {
  readonly open: boolean;
  /** When provided, the dialog is in edit mode (code read-only). */
  readonly badge?: BadgeCatalogEntry | null;
  readonly onSave: (input: UpsertBadgeCatalogInput) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function BadgeCatalogEditDialog({
  open,
  badge,
  onSave,
  onClose,
  errorMessage,
}: BadgeCatalogEditDialogProps) {
  const { t } = useI18n();
  const isEdit = Boolean(badge);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconCode, setIconCode] = useState('star');
  const [category, setCategory] = useState<BadgeCategory>('award');
  const [sportScope, setSportScope] = useState<readonly SportName[] | null>(null);
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (badge) {
      setCode(badge.code);
      setName(badge.name.en ?? '');
      setDescription(badge.description?.en ?? '');
      setIconCode(badge.iconCode);
      setCategory(badge.badgeCategory);
      setSportScope(badge.sportScope ?? null);
      setActive(badge.active);
      setSortOrder(badge.sortOrder);
    } else {
      setCode('');
      setName('');
      setDescription('');
      setIconCode('star');
      setCategory('award');
      setSportScope(null);
      setActive(true);
      setSortOrder(0);
    }
  }, [open, badge]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const payload: UpsertBadgeCatalogInput = useMemo(
    () => ({
      code,
      name: { en: name },
      ...(description.trim() ? { description: { en: description } } : {}),
      iconCode,
      badgeCategory: category,
      ...(sportScope !== null ? { sportScope: [...sportScope] } : {}),
      active,
      sortOrder,
    }),
    [code, name, description, iconCode, category, sportScope, active, sortOrder],
  );

  // sportScope (when restricted) must be non-empty per the Zod `.min(1)`.
  const sportScopeValid = sportScope === null || sportScope.length >= 1;
  const valid = useMemo(
    () => sportScopeValid && UpsertBadgeCatalogInputSchema.safeParse(payload).success,
    [payload, sportScopeValid],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSave(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const categoryOptions = useMemo(
    () =>
      BADGE_CATEGORY_VALUES.map((c) => ({
        value: c,
        label: t(`admin:recommendation.catalog.category.${c}`, { defaultValue: c }),
      })),
    [t],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={
        isEdit
          ? t('admin:recommendation.catalog.badges.edit_title', { defaultValue: 'Edit badge' })
          : t('admin:recommendation.catalog.badges.add_title', { defaultValue: 'Add badge' })
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="badge-edit-cancel"
          >
            {t('admin:recommendation.catalog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="badge-edit-submit"
          >
            {t('admin:recommendation.catalog.save', { defaultValue: 'Save' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="badge-edit-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}

        <FormField
          label={t('recommendation:catalog.code_label', { defaultValue: 'Code' })}
          required
          helper={t('admin:recommendation.catalog.code_helper', {
            defaultValue: 'Lowercase letters, numbers and underscores.',
          })}
        >
          <TextInput
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isEdit}
            placeholder={t('admin:recommendation.catalog.code_placeholder_badge', {
              defaultValue: 'most_improved',
            })}
            data-testid="badge-edit-code"
          />
        </FormField>

        <FormField
          label={t('recommendation:catalog.name_label', { defaultValue: 'Name' })}
          required
        >
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('admin:recommendation.catalog.name_placeholder_badge', {
              defaultValue: 'Most Improved',
            })}
            data-testid="badge-edit-name"
          />
        </FormField>

        <FormField
          label={t('recommendation:catalog.description_label', { defaultValue: 'Description' })}
        >
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={280}
            data-testid="badge-edit-description"
          />
        </FormField>

        <FormField
          label={t('recommendation:catalog.icon_label', { defaultValue: 'Icon' })}
          required
        >
          <BadgeIconPicker value={iconCode} onChange={setIconCode} disabled={submitting} />
        </FormField>

        <FormField
          label={t('recommendation:catalog.category_label', { defaultValue: 'Category' })}
          required
        >
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as BadgeCategory)}
            options={categoryOptions}
            data-testid="badge-edit-category"
          />
        </FormField>

        <FormField
          label={t('recommendation:catalog.sport_scope_label', { defaultValue: 'Sport scope' })}
        >
          <SportScopePicker value={sportScope} onChange={setSportScope} disabled={submitting} />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label={t('recommendation:catalog.sort_order_label', { defaultValue: 'Sort order' })}
          >
            <NumberInput
              value={sortOrder}
              onChange={setSortOrder}
              min={0}
              data-testid="badge-edit-sort"
            />
          </FormField>
          <FormField label={t('recommendation:catalog.active_label', { defaultValue: 'Active' })}>
            <Switch
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="badge-edit-active"
            />
          </FormField>
        </div>
      </div>
    </Modal>
  );
}
